import { useCallback, useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useQuery } from '@tanstack/react-query';
import {
  AccountSchema,
  EmployeeSchema,
  ReferenceMasterSchema,
  SupplierSchema,
  type AccountDto,
  type EmployeeDto,
  type ReferenceMasterDto,
  type SupplierDto,
} from '@taqon/contracts';
import type { z } from 'zod';
import { getPage } from '../../shared/api/client';

/**
 * Pickers for real master data, searched on the server. A picker never loads a whole master into
 * the browser: it asks the API for the first matches of what the person typed (SDD §10.4), so an
 * approved COA of hundreds of accounts is as reachable as a synthetic one of eight.
 */

const PAGE = 25;

/** The value typed, settled for a moment before it is sent. */
function useSettled(value: string, delayMs = 250): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => {
      setSettled(value);
    }, delayMs);
    return () => {
      clearTimeout(timer);
    };
  }, [value, delayMs]);
  return settled;
}

export function SearchPicker<T extends { id: string }>({
  label,
  required,
  value,
  onChange,
  path,
  schema,
  describe,
  queryKey,
  disabled,
  helperText,
  error,
}: {
  label: string;
  required?: boolean | undefined;
  value: T | null;
  onChange: (value: T | null) => void;
  /** The list endpoint and its fixed filters; the search term is appended as `filter[<key>]`. */
  path: (term: string) => string;
  schema: z.ZodType<T>;
  describe: (option: T) => string;
  queryKey: string;
  disabled?: boolean | undefined;
  helperText?: string | undefined;
  error?: boolean | undefined;
}) {
  // The search term is what the person typed; picking an option (MUI's `reset`) starts afresh.
  const [input, setInput] = useState('');
  const term = useSettled(input.trim());
  const onInputChange = useCallback((_: unknown, next: string, reason: string) => {
    setInput(reason === 'input' ? next : '');
  }, []);
  const sameRecord = useCallback((a: T, b: T) => a.id === b.id, []);
  const keepAll = useCallback((all: T[]) => all, []);
  const options = useQuery({
    queryKey: ['picker', queryKey, term],
    queryFn: () => getPage(path(term), schema),
  });
  // The chosen record stays selectable even when the current search would not find it.
  const found: readonly T[] = options.data?.items ?? [];
  const list = value !== null && !found.some((o) => o.id === value.id) ? [value, ...found] : found;
  return (
    <Autocomplete<T>
      options={list}
      value={value}
      disabled={disabled}
      onChange={(_, next) => {
        onChange(next);
      }}
      onInputChange={onInputChange}
      filterOptions={keepAll}
      getOptionLabel={describe}
      isOptionEqualToValue={sameRecord}
      loading={options.isPending}
      noOptionsText={term === '' ? 'Type to search' : 'No match'}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          size="small"
          error={error}
          helperText={options.isError ? options.error.message : helperText}
        />
      )}
    />
  );
}

// Stable across renders, so the autocomplete never mistakes a re-render for a new selection.
/** The account by code and name, with the L4 group it sits in (FRD-FIN-011). */
const describeAccount = (a: AccountDto) => {
  const group = a.path.at(-1);
  return group === undefined ? `${a.code} — ${a.name}` : `${a.code} — ${a.name} (${group.name})`;
};
const describeSupplier = (s: SupplierDto) => `${s.code} — ${s.companyName}`;
const describeEmployee = (e: EmployeeDto) => `${e.code} — ${e.name}`;

const search = (key: string, term: string) =>
  term === '' ? '' : `&filter[${key}]=${encodeURIComponent(term)}`;

/** Active posting accounts of the COA, by code prefix or any part of the name (FRD-FIN-071). */
export function AccountPicker(props: {
  value: AccountDto | null;
  onChange: (value: AccountDto | null) => void;
  error?: boolean | undefined;
  helperText?: string | undefined;
}) {
  return (
    <SearchPicker
      label="Account"
      required
      queryKey="accounts"
      schema={AccountSchema}
      path={(term) =>
        `/api/v1/ledger/accounts?pageSize=${String(PAGE)}&sort=code&filter[status]=active` +
        `&filter[posting]=true${search('search', term)}`
      }
      describe={describeAccount}
      {...props}
    />
  );
}

/** Active suppliers only: an inactive supplier is not usable in new work (FRD-PRC-012). */
export function SupplierPicker(props: {
  value: SupplierDto | null;
  onChange: (value: SupplierDto | null) => void;
  /** Required unless the caller says otherwise (an asset's supplier is optional). */
  required?: boolean;
  disabled?: boolean | undefined;
  error?: boolean | undefined;
  helperText?: string | undefined;
}) {
  return (
    <SearchPicker
      label="Supplier"
      required
      queryKey="suppliers-active"
      schema={SupplierSchema}
      path={(term) =>
        `/api/v1/procurement/suppliers?pageSize=${String(PAGE)}&sort=code` +
        `&filter[status]=active${search('search', term)}`
      }
      describe={describeSupplier}
      {...props}
    />
  );
}

/** Active employees, by name (the Employee dimension, FRD-HRP-004). */
export function EmployeePicker(props: {
  /** "Employee" unless the caller names the role, e.g. "Custodian". */
  label?: string;
  value: EmployeeDto | null;
  onChange: (value: EmployeeDto | null) => void;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  helperText?: string | undefined;
  error?: boolean | undefined;
}) {
  return (
    <SearchPicker
      label="Employee"
      queryKey="employees-active"
      schema={EmployeeSchema}
      path={(term) =>
        `/api/v1/hr/employees?pageSize=${String(PAGE)}&sort=name` +
        `&filter[status]=active${search('name', term)}`
      }
      describe={describeEmployee}
      {...props}
    />
  );
}

/** Small organization masters (cost centres, locations): the active ones, as a plain select. */
export function useActiveMasters(path: string, enabled: boolean) {
  return useQuery({
    queryKey: ['masters-active', path],
    enabled,
    queryFn: () =>
      getPage(`${path}?pageSize=200&sort=code&filter[status]=active`, ReferenceMasterSchema),
  });
}

export function MasterSelect({
  label,
  value,
  onChange,
  options,
  required,
  disabled,
  helperText,
  error,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly ReferenceMasterDto[];
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  helperText?: string | undefined;
  error?: boolean | undefined;
}) {
  return (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      required={required}
      disabled={disabled}
      error={error}
      helperText={helperText}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      sx={{ minWidth: 200 }}
    >
      <MenuItem value="">None</MenuItem>
      {options.map((o) => (
        <MenuItem key={o.id} value={o.id}>
          {o.code} — {o.name}
        </MenuItem>
      ))}
    </TextField>
  );
}
